import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import './TagManagement.css';

// 12 color options
const COLOR_OPTIONS = [
  '#FFFFFF', // White
  '#E0E0E0', // Light gray
  '#9E9E9E', // Medium gray
  '#424242', // Very dark gray
  '#000000', // Black
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#F7DC6F', // Yellow
  '#BB8FCE', // Purple
  '#F8B739', // Orange
  '#52BE80', // Green
];

function TagManagement({ onClose }) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    is_global: false,
    color: COLOR_OPTIONS[2], // Default to medium gray
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { tag, connectedTrades, loading }

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/portfolio/tags/?page_size=all`);
      const data = response.data.results || response.data;
      setTags(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Error fetching tags: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      // Prepare data for submission - exclude user field as backend sets it automatically
      const submitData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        is_global: formData.is_global,
        color: formData.color,
      };
      
      if (editingTag) {
        await axios.patch(`${API_URL}/portfolio/tags/${editingTag.id}/`, submitData);
      } else {
        await axios.post(`${API_URL}/portfolio/tags/`, submitData);
      }
      await fetchTags();
      resetForm();
    } catch (err) {
      // Show detailed error message
      let errorMessage = 'Error saving tag: ';
      if (err.response?.data) {
        if (err.response.data.detail) {
          errorMessage += err.response.data.detail;
        } else if (err.response.data.name) {
          errorMessage += `Name: ${Array.isArray(err.response.data.name) ? err.response.data.name.join(', ') : err.response.data.name}`;
        } else if (err.response.data.non_field_errors) {
          errorMessage += Array.isArray(err.response.data.non_field_errors) 
            ? err.response.data.non_field_errors.join(', ') 
            : err.response.data.non_field_errors;
        } else {
          errorMessage += JSON.stringify(err.response.data);
        }
      } else {
        errorMessage += err.message;
      }
      setError(errorMessage);
      console.error('Tag save error:', err.response?.data || err);
    }
  };

  const handleDeleteClick = async (tag) => {
    // Fetch connected trades first
    setDeleteConfirm({ tag, connectedTrades: null, loading: true });
    try {
      const response = await axios.get(`${API_URL}/portfolio/tags/${tag.id}/connected_trades/`);
      setDeleteConfirm({ 
        tag, 
        connectedTrades: response.data, 
        loading: false 
      });
    } catch (err) {
      console.error('Error fetching connected trades:', err);
      setDeleteConfirm({ 
        tag, 
        connectedTrades: { trade_count: 0, trades: [] }, 
        loading: false 
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    
    const tagId = deleteConfirm.tag.id;
    try {
      setError(null);
      await axios.delete(`${API_URL}/portfolio/tags/${tagId}/`);
      await fetchTags();
      if (editingTag && editingTag.id === tagId) {
        resetForm();
      }
      setDeleteConfirm(null);
    } catch (err) {
      setError('Error deleting tag: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name || '',
      description: tag.description || '',
      category: tag.category || '',
      is_global: tag.is_global || false,
      color: tag.color || COLOR_OPTIONS[2],
    });
  };

  const resetForm = () => {
    setEditingTag(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      is_global: false,
      color: COLOR_OPTIONS[2],
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  return (
    <div className="tag-management-page">
      <div className="tag-management-container">
        <div className="tag-management-header">
          <h2>Manage Tags</h2>
          <button className="back-button" onClick={onClose}>← Back</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="tag-management-content">
          <div className="tag-form-section">
            <h3>{editingTag ? 'Edit Tag' : 'Create New Tag'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category</label>
                <input
                  type="text"
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label htmlFor="color">Color</label>
                <div className="color-picker">
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`color-option ${formData.color === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData(prev => ({ ...prev, color }))}
                      title={color}
                    />
                  ))}
                </div>
                <div className="color-preview">
                  <span 
                    className="color-preview-badge"
                    style={{ backgroundColor: formData.color }}
                  >
                    {formData.name || 'Preview'}
                  </span>
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="is_global"
                    checked={formData.is_global}
                    onChange={handleInputChange}
                  />
                  Global tag (visible to all users)
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingTag ? 'Update Tag' : 'Create Tag'}
                </button>
                {editingTag && (
                  <button type="button" className="btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="tag-list-section">
            <h3>Existing Tags</h3>
            {loading ? (
              <div className="loading">Loading tags...</div>
            ) : tags.length === 0 ? (
              <div className="empty-state">No tags found. Create your first tag!</div>
            ) : (
              <div className="tag-list">
                {tags.map((tag) => (
                  <div key={tag.id} className="tag-item">
                    <span 
                      className="tag-badge"
                      style={{ backgroundColor: tag.color }}
                    >
                      {tag.name}
                    </span>
                    <div className="tag-info">
                      {tag.description && <div className="tag-description">{tag.description}</div>}
                      {tag.category && <div className="tag-group">Category: {tag.category}</div>}
                      <div className="tag-meta">
                        {tag.is_global ? 'Global' : 'Personal'} • {tag.user_username || 'Unknown'}
                      </div>
                    </div>
                    <div className="tag-actions">
                      <button 
                        className="btn-edit"
                        onClick={() => handleEdit(tag)}
                      >
                        Edit
                      </button>
                      {!tag.is_global && (
                        <button 
                          className="btn-delete"
                          onClick={() => handleDeleteClick(tag)}
                        >
                          Delete
                        </button>
                      )}
                      {tag.is_global && (
                        <span className="delete-disabled" title="Global tags can only be deleted in admin">
                          Delete (Admin only)
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="delete-confirm-overlay" onClick={handleDeleteCancel}>
          <div className="delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Tag: {deleteConfirm.tag.name}</h3>
            {deleteConfirm.loading ? (
              <div className="loading">Loading connected trades...</div>
            ) : (
              <>
                <p>
                  Are you sure you want to delete this tag? 
                  {deleteConfirm.connectedTrades?.trade_count > 0 && (
                    <strong> This tag will be removed from {deleteConfirm.connectedTrades.trade_count} trade(s).</strong>
                  )}
                </p>
                {deleteConfirm.connectedTrades?.trade_count > 0 && (
                  <div className="connected-trades-list">
                    <h4>Connected Trades ({deleteConfirm.connectedTrades.trade_count}):</h4>
                    <div className="trades-scroll-container">
                      {deleteConfirm.connectedTrades.trades && deleteConfirm.connectedTrades.trades.length > 0 ? (
                        <ul>
                          {deleteConfirm.connectedTrades.trades.map((trade) => (
                            <li key={trade.id}>
                              <strong>{trade.symbol}</strong> - {trade.description || 'No description'} 
                              <span className="trade-date"> ({trade.trade_date})</span>
                              <span className="trade-account"> - Account: {trade.account__broker_account_id}</span>
                            </li>
                          ))}
                          {deleteConfirm.connectedTrades.trade_count > deleteConfirm.connectedTrades.trades.length && (
                            <li className="more-trades">
                              ... and {deleteConfirm.connectedTrades.trade_count - deleteConfirm.connectedTrades.trades.length} more trade(s)
                            </li>
                          )}
                        </ul>
                      ) : (
                        <p>No trades found (this may be due to permissions or filters).</p>
                      )}
                    </div>
                  </div>
                )}
                {deleteConfirm.connectedTrades?.trade_count === 0 && (
                  <p className="no-trades-message">This tag is not connected to any trades.</p>
                )}
                <div className="delete-confirm-actions">
                  <button 
                    className="btn-delete-confirm"
                    onClick={handleDeleteConfirm}
                  >
                    Yes, Delete Tag
                  </button>
                  <button 
                    className="btn-cancel"
                    onClick={handleDeleteCancel}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default TagManagement;

